import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api.js';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';

/**
 * Downloads — the case's exports, kept until a person deletes them
 * (plans/BY_DOCUMENT_INVENTORY_VIEW_PROPOSAL.md §10, 2026-10-04).
 *
 * The "ready" notice and the banner are moments; this is the place. Every
 * export of the case is a row: waiting to start, being prepared (N of M),
 * ready, or failed and why. A ready one is downloaded with a link the server
 * makes at that click (the same fresh sign-in as exporting), says when the case
 * has moved on since it was made — documents in it deleted from the case,
 * documents added to what it covers — and stays until someone deletes it.
 *
 * `DownloadsHost` (mounted once per case in App.jsx) opens it on the window
 * event `dc:open-downloads`; `dc:exports-changed` tells whoever shows a count.
 */
export const OPEN_DOWNLOADS = 'dc:open-downloads';
export const EXPORTS_CHANGED = 'dc:exports-changed';

export function openDownloads() {
  window.dispatchEvent(new CustomEvent(OPEN_DOWNLOADS));
}

const fmtBytes = (n) => {
  if (!n) return '';
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return sameDay ? `today ${time}` : `${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} ${time}`;
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Start a download without opening a tab (a pop-up blocker has nothing to refuse, and a
 *  link that fails cannot take the app's own page away): a hidden frame, removed later. */
export function quietDownload(url) {
  const f = document.createElement('iframe');
  f.style.display = 'none';
  f.src = url;
  document.body.appendChild(f);
  setTimeout(() => f.remove(), 120000);
}

/** What has changed in the case since a ready export was made, in words; '' when nothing has. */
export function movedOnText(m) {
  if (!m || (!m.deleted && !m.added && !m.moved && !m.moved_in)) return '';
  const parts = [];
  if (m.deleted) parts.push(`${plural(m.deleted, 'document in it has', 'documents in it have')} since been deleted from the case`);
  if (m.added) parts.push(`${plural(m.added, 'document was', 'documents were')} added since`);
  // the zip is laid out in the folders of the day it was made
  if (m.moved) parts.push(`${plural(m.moved, 'document in it is', 'documents in it are')} filed in a different folder now`);
  if (m.moved_in) parts.push(`${plural(m.moved_in, 'document was', 'documents were')} filed into this folder since`);
  return `Out of date: ${parts.join('; ')}.`;
}

/** The case's exports + how many are ready; refetches on demand and when an export job changes. */
export function useCaseExports(caseId, { live = false } = {}) {
  const [state, setState] = useState({ exports: null, ready: 0, error: null });
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const refresh = useCallback(async () => {
    if (!caseId) return;
    try {
      const r = await api.get(`/documents/case/${caseId}/exports`);
      if (alive.current) setState({ exports: r.exports || [], ready: r.ready || 0, error: null });
    } catch (e) {
      if (alive.current) setState((s) => ({ ...s, exports: s.exports || [], error: e.message || 'Could not load the downloads.' }));
    }
  }, [caseId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    window.addEventListener(EXPORTS_CHANGED, refresh);
    return () => window.removeEventListener(EXPORTS_CHANGED, refresh);
  }, [refresh]);

  // While the list is on screen and something is still waiting or being prepared, keep it current.
  const busy = (state.exports || []).some((e) => e.state === 'waiting' || e.state === 'preparing');
  useEffect(() => {
    if (!live || !busy) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [live, busy, refresh]);

  return { ...state, refresh };
}

function StateChip({ e }) {
  const base = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' };
  if (e.state === 'waiting') return <span style={{ ...base, background: '#eef0f4', color: '#556' }}>Waiting to start</span>;
  if (e.state === 'preparing') {
    const p = e.progress || {};
    return (
      <span style={{ ...base, background: '#e8f0fe', color: '#1a56b0' }}>
        Preparing{p.total ? ` ${p.done || 0} of ${p.total}` : '…'}
      </span>
    );
  }
  if (e.state === 'failed') return <span style={{ ...base, background: '#fdecea', color: '#b3261e' }}>Failed</span>;
  if (!e.available) return <span style={{ ...base, background: '#f3f3f3', color: '#777' }}>No longer in storage</span>;
  return <span style={{ ...base, background: '#e6f4ea', color: '#1e7a3c' }}>Ready</span>;
}

export default function DownloadsPanel({ caseId, onClose }) {
  const { exports, error, refresh } = useCaseExports(caseId, { live: true });
  const { jobs } = useBackgroundJobs();
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState({});
  const [links, setLinks] = useState({});          // export id → the link just made, to click if nothing started
  const [confirming, setConfirming] = useState(null);

  // The case-wide poller sees an export start and finish: bring the list along.
  const exportJobs = (jobs || []).filter((j) => j.job_type === 'export_documents').map((j) => `${j.job_id}:${j.status}`).join(',');
  useEffect(() => { refresh(); }, [exportJobs, refresh]);

  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function download(e) {
    setBusyId(e.id);
    setRowError((m) => ({ ...m, [e.id]: null }));
    try {
      // The link is made now, for this click, and is good for an hour.
      const r = await api.post(`/documents/case/${caseId}/exports/${e.id}/download`, {});
      quietDownload(r.download_url);
      setLinks((m) => ({ ...m, [e.id]: r.download_url }));
    } catch (err) {
      setRowError((m) => ({ ...m, [e.id]: err.message || 'Could not start the download.' }));
      refresh();
    } finally {
      setBusyId(null);
    }
  }

  async function remove(e) {
    setBusyId(e.id);
    setRowError((m) => ({ ...m, [e.id]: null }));
    try {
      await api.del(`/documents/case/${caseId}/exports/${e.id}`);
      setConfirming(null);
      await refresh();
      window.dispatchEvent(new CustomEvent(EXPORTS_CHANGED));
    } catch (err) {
      setRowError((m) => ({ ...m, [e.id]: err.message || 'Could not delete the export.' }));
    } finally {
      setBusyId(null);
    }
  }

  const row = { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid #eee' };
  const muted = { fontSize: 12, color: '#667', marginTop: 2 };

  return createPortal((
    <div className="np-export-overlay" onClick={onClose}>
      <div
        className="np-export-modal" data-testid="downloads-panel" onClick={(ev) => ev.stopPropagation()}
        style={{ maxWidth: 640, height: 'auto', maxHeight: '80vh' }}
      >
        <div className="np-export-head">
          <h3>Downloads</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="np-export-body" style={{ display: 'block', padding: '4px 16px 12px', overflowY: 'auto' }}>
          <p style={{ fontSize: 12, color: '#667', margin: '8px 0 4px' }}>
            Exports of this case. Each stays here until someone deletes it.
          </p>
          {exports === null && <div style={{ padding: 16, color: '#888', fontSize: 13 }}>Loading…</div>}
          {error && <div className="bulk-popover-error" style={{ margin: '8px 0' }}>{error}</div>}
          {exports && exports.length === 0 && !error && (
            <div data-testid="downloads-empty" style={{ padding: '20px 0', color: '#667', fontSize: 13 }}>
              Nothing has been exported from this case yet. Use “Export all files”, a folder’s menu, a selection, or a
              file’s “Documents in this file”.
            </div>
          )}
          {(exports || []).map((e) => {
            const out = e.state === 'ready' ? movedOnText(e.moved_on) : '';
            const canDownload = e.state === 'ready' && e.available;
            const canDelete = e.state === 'ready' || e.state === 'failed';
            return (
              <div key={e.id} style={row} data-testid="downloads-row" data-export-id={e.id} data-state={e.state}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 13 }}>{e.label}</b>
                    <StateChip e={e} />
                  </div>
                  <div style={muted}>
                    {e.state === 'ready' && e.document_count != null && (
                      <>{plural(e.document_count, e.format === 'package' ? 'document' : 'file', e.format === 'package' ? 'documents' : 'files')}
                        {e.total_bytes ? ` · ${fmtBytes(e.total_bytes)}` : ''} · </>
                    )}
                    {fmtWhen(e.ready_at || e.requested_at)}
                    {e.requested_by ? ` · ${e.mine ? 'you' : e.requested_by}` : (e.mine ? ' · you' : '')}
                  </div>
                  {e.state === 'ready' && e.not_included > 0 && (
                    <div style={muted}>{plural(e.not_included, 'document', 'documents')} could not be included (listed in the package).</div>
                  )}
                  {out && <div data-testid="downloads-out-of-date" style={{ ...muted, color: '#9a5b00' }}>⚠ {out}</div>}
                  {e.state === 'ready' && !e.available && (
                    <div style={muted}>The file is gone from storage. Delete this entry and export again.</div>
                  )}
                  {e.state === 'failed' && <div style={{ ...muted, color: '#b3261e' }}>{e.error}</div>}
                  {links[e.id] && (
                    <div style={muted} data-testid="downloads-started">
                      The download has started. If nothing happened,{' '}
                      <a href={links[e.id]} target="_blank" rel="noopener noreferrer">click here</a>.
                    </div>
                  )}
                  {rowError[e.id] && <div style={{ ...muted, color: '#b3261e' }}>{rowError[e.id]}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  {confirming === e.id ? (
                    <>
                      <span style={{ fontSize: 12, color: '#555' }}>Delete this export?</span>
                      <button className="btn btn-sm" data-testid="downloads-delete-confirm" disabled={busyId === e.id} onClick={() => remove(e)}>
                        {busyId === e.id ? 'Deleting…' : 'Delete'}
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={() => setConfirming(null)}>Keep</button>
                    </>
                  ) : (
                    <>
                      {canDownload && (
                        <button className="btn btn-sm btn-primary" data-testid="downloads-download" disabled={busyId === e.id} onClick={() => download(e)}>
                          {busyId === e.id ? 'Starting…' : 'Download'}
                        </button>
                      )}
                      {canDelete && (
                        <button className="btn btn-sm btn-ghost" data-testid="downloads-delete" disabled={busyId === e.id} onClick={() => setConfirming(e.id)}>
                          Delete
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  ), document.body);
}

/** Mounted once per case (App.jsx): opens the panel wherever "Downloads" is asked for. */
export function DownloadsHost({ caseId }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_DOWNLOADS, onOpen);
    return () => window.removeEventListener(OPEN_DOWNLOADS, onOpen);
  }, []);
  if (!open || !caseId) return null;
  return <DownloadsPanel caseId={caseId} onClose={() => setOpen(false)} />;
}
